import { useEffect, useState } from "react";
import { Wallet, Loader2, FileText, ChevronRight } from "lucide-react";
import api from "@/lib/api";

const KIND_LABEL = { cash_china: "🛫 Cash Chine", exchange: "🔄 Change de devises" };

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

export default function MyDeposits() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get("/deposits/mine")
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="mt-12" data-testid="my-deposits">
      <div className="flex items-center gap-3 mb-4">
        <Wallet className="text-[var(--accent)]" />
        <h2 className="font-display text-2xl font-black">Mes transferts d'argent</h2>
        <span className="ml-2 text-sm text-[var(--ink-soft)]">{items.length}</span>
      </div>

      {loading ? (
        <div className="card-flat p-10 flex items-center justify-center">
          <Loader2 className="animate-spin text-[var(--ink-soft)]" />
        </div>
      ) : items.length === 0 ? (
        <div className="card-flat p-8 text-center">
          <Wallet className="mx-auto text-[var(--ink-soft)]" size={30} />
          <p className="text-[var(--ink-soft)] mt-3">Aucun transfert d'argent enregistré.</p>
          <p className="text-xs text-[var(--ink-soft)] mt-2">
            Dès qu'un dépôt est enregistré au bureau Dakar avec votre email/téléphone, il apparaîtra ici.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((d) => {
            const isOpen = expanded === d.id;
            return (
              <li key={d.id} className="card-flat overflow-hidden" data-testid={`my-deposit-${d.reference}`}>
                <button
                  onClick={() => setExpanded(isOpen ? null : d.id)}
                  className="w-full text-left p-5 flex items-center gap-4 hover:bg-[var(--bg-soft)] transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-[var(--bg-soft)] flex items-center justify-center flex-shrink-0">
                    <Wallet size={18} className="text-[var(--accent)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-mono font-bold text-sm">{d.reference}</p>
                      <span className="text-[10px] font-bold uppercase tracking-widest bg-[var(--secondary)] text-white px-2 py-0.5 rounded-full">{KIND_LABEL[d.kind]}</span>
                      <span className="text-[10px] font-bold text-[var(--accent)] bg-[var(--accent)]/10 px-2 py-0.5 rounded-full">
                        {Math.round(d.deposit_amount).toLocaleString("fr-FR")} {d.deposit_currency}
                      </span>
                    </div>
                    <p className="text-xs text-[var(--ink-soft)] mt-1 truncate">
                      {formatDate(d.created_at)}
                      {d.pickup_city ? ` · Retrait à ${d.pickup_city}` : ""}
                    </p>
                  </div>
                  <ChevronRight size={18} className={`transition-transform text-[var(--ink-soft)] ${isOpen ? "rotate-90" : ""}`} />
                </button>

                {isOpen && (
                  <div className="border-t border-black/5 p-5 space-y-4 bg-[var(--bg-soft)]/40">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-[var(--ink-soft)]">Déposé à Dakar</p>
                        <p className="font-bold mt-0.5">{Math.round(d.deposit_amount).toLocaleString("fr-FR")} {d.deposit_currency}</p>
                      </div>
                      {d.delivery_amount != null && (
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-[var(--ink-soft)]">À recevoir</p>
                          <p className="font-bold mt-0.5">{Math.round(d.delivery_amount).toLocaleString("fr-FR")} {d.delivery_currency}</p>
                        </div>
                      )}
                      {d.exchange_rate && (
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-[var(--ink-soft)]">Taux appliqué</p>
                          <p className="font-bold mt-0.5">{d.exchange_rate}</p>
                        </div>
                      )}
                      {d.pickup_city && (
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-[var(--ink-soft)]">Ville de retrait</p>
                          <p className="font-bold mt-0.5">{d.pickup_city}</p>
                        </div>
                      )}
                    </div>
                    {d.notes && <p className="text-sm text-[var(--ink-soft)] italic">« {d.notes} »</p>}
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/deposits/${d.reference}/receipt.pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-outline text-xs !py-1.5 !px-3 inline-flex"
                      data-testid={`open-deposit-pdf-${d.reference}`}
                    >
                      <FileText size={13} /> Reçu PDF signé
                    </a>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
